import { useMemo } from "react"
import { useForm, useWatch } from "react-hook-form"
import type { TCustomOfferFormValues } from "../CustomOfferForm"
import type { IContractType, IPlan } from "@apis/offers/types"
import type { IMarketRate } from "@apis/pricing/types"

type UseCustomOfferFormLogicProps = {
  contractTypes: IContractType[] | undefined
  marketRates: IMarketRate[] | undefined
  allPlans?: IPlan[] | undefined
}

export const useCustomOfferFormLogic = ({
  contractTypes,
  marketRates,
  allPlans,
}: UseCustomOfferFormLogicProps) => {
  const form = useForm<TCustomOfferFormValues>({
    mode: "onChange",
    defaultValues: {
      contract_type_code: "",
      plan_duration: "",
      market_rate_id: "",
      plan_code: "",
    },
  })

  const selectedContractTypeCode = useWatch({
    control: form.control,
    name: "contract_type_code",
  })

  const selectedPlanDuration = useWatch({
    control: form.control,
    name: "plan_duration",
  })

  const selectedMarketRateId = useWatch({
    control: form.control,
    name: "market_rate_id",
  })

  const selectedPlanCode = useWatch({
    control: form.control,
    name: "plan_code",
  })

  // Derived data with memoization
  const selectedContractType = useMemo(
    () =>
      contractTypes?.find(
        (type) => type.contract_type_code === selectedContractTypeCode,
      ),
    [contractTypes, selectedContractTypeCode],
  )

  const selectedMarketRate = useMemo(
    () =>
      marketRates?.find(
        (rate) => String(rate.market_rate_id) === selectedMarketRateId,
      ),
    [marketRates, selectedMarketRateId],
  )

  const selectedPlan = useMemo(
    () => allPlans?.find((plan) => plan.plan_code === selectedPlanCode),
    [allPlans, selectedPlanCode],
  )

  const filteredRates = useMemo(
    () =>
      marketRates?.filter((market_rate) => {
        return (
          selectedContractType?.unit_type.toLowerCase() ===
          market_rate.unit_type.toLowerCase()
        )
      }),
    [marketRates, selectedContractType?.unit_type],
  )

  const filteredPlans = useMemo(
    () =>
      allPlans?.filter((plan) => {
        return (
          plan.contract_type_code.toLowerCase() ===
            selectedContractType?.contract_type_code.toLowerCase() &&
          plan.contract_duration.toLowerCase() ===
            selectedPlanDuration.toLowerCase()
        )
      }),
    [allPlans, selectedContractType?.contract_type_code, selectedPlanDuration],
  )

  const planSearchFilter = (value: string, search: string) => {
    const plan = filteredPlans?.find((plan) => plan.plan_code === value)
    if (!plan) return 0

    const searchLower = search.toLowerCase()
    const matches =
      plan.plan_code.toLowerCase().includes(searchLower) ||
      plan.plan_name.toLowerCase().includes(searchLower)

    return matches ? 1 : 0
  }

  const marketRateSearchFilter = (value: string, search: string) => {
    const marketRate = filteredRates?.find(
      (marketRate) => marketRate.market_rate_id === Number(value),
    )
    const matches = marketRate?.rate.includes(search)
    return matches ? 1 : 0
  }

  return {
    // Form instance
    form,
    // Selected values
    selectedContractType,
    selectedPlanDuration,
    selectedMarketRate,
    selectedPlan,
    // Filtered data
    filteredRates,
    filteredPlans,
    // Utilities
    planSearchFilter,
    marketRateSearchFilter,
  }
}
